// components/RecastZonePickOverlay.tsx
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Check, Loader2, RefreshCw, X } from '@/components/ui/lucide-memo';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { buildLiveScreenSize, buildLiveScreenX, buildLiveScreenY } from '#lib/layer-geometry';
import { getProjectPanOffset, getProjectZoomLevel } from '#lib/project-runtime-layer-ui-state';
import { fitRecastZone, RECAST_EXTRAS_RECIPE, recastExtrasErrors, recastReadingOrder, recastZoneFromDrag } from '#lib/recastExtras';
import type { RecastZone } from '#types/types';
import { OverlayActionButton } from './NativeLayerCutOverlay';
import CreditsBadge from './ui/CreditsBadge';
import { PICK_FLOATING_UI_STYLE, PICK_INTERACTION_STYLE, pickToolbarStyle, quoteRecipeCredits, usePickEscape, useProjectViewport } from './workspacePickOverlay';

/** Below this travel, in screen pixels, a press is a click; a drawn square under it is dropped. */
const DRAG_PX = 4;

const translations = {
  en: {
    zones: (enabled: number, all: number) => `${enabled} of ${all} ${all === 1 ? 'zone' : 'zones'}`,
    note: (zone: number) => `Zone ${zone}: optional words`,
    recast: (count: number) => `Recast ${count} ${count === 1 ? 'zone' : 'zones'}`,
    detectAgain: 'Detect the people again, replacing these zones',
    detecting: 'Detecting people…',
    cancel: 'Cancel',
  },
  fr: {
    zones: (enabled: number, all: number) => `${enabled} sur ${all} ${all === 1 ? 'zone' : 'zones'}`,
    note: (zone: number) => `Zone ${zone} : quelques mots, facultatif`,
    recast: (count: number) => `Refaire ${count} ${count === 1 ? 'zone' : 'zones'}`,
    detectAgain: 'Détecter à nouveau les personnes, en remplaçant ces zones',
    detecting: 'Détection des personnes…',
    cancel: 'Annuler',
  },
} as const;

type Gesture = { start: { x: number; y: number }; hit: number | null; origin: RecastZone | null; moved: boolean };

/**
 * The open Recast extras pick (useCanvas: openRecastZonePick). A click on a zone turns it on or off and opens its note,
 * a drag moves it, a drag on the rest of the frame draws a new one; the toolbar beside the frame shows the price and
 * recasts. Escape leaves. The middle button still pans the canvas beneath.
 */
const RecastZonePickOverlay = React.memo(() => {
  const { kernel } = useKernel();
  const pick = useKernelStore((state) => state.recastZonePick);
  const frame = useKernelStore((state) => (
    state.recastZonePick ? state.inventory.StudioWorkspace.registry[state.recastZonePick.workspaceId]?.frameRect ?? null : null
  ));
  const { zoom, panX, panY } = useProjectViewport(pick?.projectId ?? null);
  const language = useKernelStore((state) => state.language);
  const t = translations[language];
  const gesture = useRef<Gesture | null>(null);
  const draft = useRef<HTMLDivElement>(null);

  usePickEscape('data-recast-zone-pick', 'cancelRecastZonePick');

  // A pick does not outlive its workspace.
  const isWorkspaceGone = pick !== null && frame === null;
  useEffect(() => {
    if (isWorkspaceGone) kernel.current?.cancelRecastZonePick();
  }, [kernel, isWorkspaceGone]);

  const zones = pick?.zones ?? null;
  const detecting = Boolean(pick?.detecting);
  const enabled = zones?.filter((zone) => zone.enabled).length ?? 0;
  const credits = useMemo(() => quoteRecipeCredits(RECAST_EXTRAS_RECIPE.model, RECAST_EXTRAS_RECIPE.params, RECAST_EXTRAS_RECIPE.prompt, enabled), [enabled]);
  // The same refusal recastExtras makes, shown before the click rather than after it.
  const errors = useMemo(() => (zones && frame ? recastExtrasErrors(zones, frame) : []), [zones, frame]);
  const geometry = useMemo(() => {
    if (!zones || !frame) return [];
    const numbers: number[] = [];
    recastReadingOrder(zones).forEach((i, position) => { numbers[i] = position + 1; });
    return zones.map((zone, i) => ({
      number: numbers[i]!,
      zone,
      box: {
        left: buildLiveScreenX(frame.x + zone.x, panX, zoom), top: buildLiveScreenY(frame.y + zone.y, panY, zoom),
        width: buildLiveScreenSize(zone.side, zoom), height: buildLiveScreenSize(zone.side, zoom),
      },
      noteStyle: {
        left: buildLiveScreenX(frame.x + zone.x + zone.side / 2, panX, zoom),
        top: `calc(${buildLiveScreenY(frame.y + zone.y + zone.side, panY, zoom)} + 6px)`,
        width: `min(220px, max(140px, ${buildLiveScreenSize(zone.side, zoom)}))`,
      },
    }));
  }, [zones, frame, panX, panY, zoom]);

  /** The pointer in frame units from the frame's top-left corner, and the screen pixels of one unit. */
  const pointAt = useCallback((clientX: number, clientY: number) => {
    const k = kernel.current;
    const current = k?.getRecastZonePick();
    const container = k?.canvasContainerRef.current;
    const rect = current ? k?.getWorkspace(current.workspaceId)?.frameRect : undefined;
    if (!k || !current || !container || !rect) return null;
    const box = container.getBoundingClientRect();
    const pan = getProjectPanOffset(k, current.projectId), scale = getProjectZoomLevel(k, current.projectId);
    const x = (clientX - box.left - pan.x) / scale - rect.x, y = (clientY - box.top - pan.y) / scale - rect.y;
    return { pick: current, frame: rect, point: { x, y }, inside: x >= 0 && x <= rect.w && y >= 0 && y <= rect.h, scale, pan };
  }, [kernel]);

  const finish = useCallback((clientX: number, clientY: number) => {
    const done = gesture.current, at = pointAt(clientX, clientY), k = kernel.current;
    gesture.current = null;
    if (draft.current) draft.current.style.display = 'none';
    if (!done || !at || !k) return;
    const { zones: current } = at.pick;
    if (!done.moved) {
      if (done.hit === null) k.updateRecastZonePick(current, null);
      else k.updateRecastZonePick(current.map((zone, i) => (i === done.hit ? { ...zone, enabled: !zone.enabled } : zone)), done.hit);
      return;
    }
    if (done.hit !== null) return;
    const zone = recastZoneFromDrag(done.start, at.point, at.frame);
    if (zone.side * at.scale >= DRAG_PX) k.updateRecastZonePick([...current, zone], current.length);
  }, [kernel, pointAt]);

  const down = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.stopPropagation();
    const at = pointAt(event.clientX, event.clientY);
    // The zones are being replaced: nothing is edited until they land.
    if (!at || at.pick.detecting) return;
    if (!at.inside) {
      kernel.current?.updateRecastZonePick(at.pick.zones, null);
      return;
    }
    const { x, y } = at.point;
    const hit = at.pick.zones.findLastIndex((zone) => x >= zone.x && x <= zone.x + zone.side && y >= zone.y && y <= zone.y + zone.side);
    gesture.current = { start: at.point, hit: hit >= 0 ? hit : null, origin: hit >= 0 ? at.pick.zones[hit]! : null, moved: false };
  }, [kernel, pointAt]);

  const move = useCallback((event: React.MouseEvent) => {
    const current = gesture.current;
    if (!current) return;
    // A release outside the canvas never reached it.
    if (!(event.buttons & 1)) {
      finish(event.clientX, event.clientY);
      return;
    }
    const at = pointAt(event.clientX, event.clientY), k = kernel.current;
    if (!at || !k) return;
    const dx = at.point.x - current.start.x, dy = at.point.y - current.start.y;
    if (!current.moved && Math.hypot(dx, dy) * at.scale < DRAG_PX) return;
    current.moved = true;
    if (current.hit !== null && current.origin) {
      const moved = fitRecastZone({ ...current.origin, x: current.origin.x + dx, y: current.origin.y + dy }, at.frame);
      k.updateRecastZonePick(at.pick.zones.map((zone, i) => (i === current.hit ? moved : zone)), current.hit);
      return;
    }
    const el = draft.current;
    if (!el) return;
    const zone = recastZoneFromDrag(current.start, at.point, at.frame);
    Object.assign(el.style, {
      display: 'block',
      left: `${at.pan.x + (at.frame.x + zone.x) * at.scale}px`,
      top: `${at.pan.y + (at.frame.y + zone.y) * at.scale}px`,
      width: `${zone.side * at.scale}px`,
      height: `${zone.side * at.scale}px`,
    });
  }, [kernel, pointAt, finish]);

  const up = useCallback((event: React.MouseEvent) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    finish(event.clientX, event.clientY);
  }, [finish]);

  const swallow = useCallback((event: React.SyntheticEvent) => event.stopPropagation(), []);

  const changeNote = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const k = kernel.current, current = k?.getRecastZonePick();
    if (!k || !current || current.selected === null) return;
    const value = event.currentTarget.value;
    k.updateRecastZonePick(current.zones.map((zone, i) => (i === current.selected ? { ...zone, note: value } : zone)), current.selected);
  }, [kernel]);

  const recast = useCallback(() => { void kernel.current?.commitRecastZonePick(); }, [kernel]);
  const detectAgain = useCallback(() => {
    const workspaceId = kernel.current?.getRecastZonePick()?.workspaceId;
    if (workspaceId) void kernel.current?.openRecastZonePick(workspaceId, true);
  }, [kernel]);
  const cancel = useCallback(() => kernel.current?.cancelRecastZonePick(), [kernel]);

  const portalHost = kernel.current?.canvasContainerRef.current ?? null;
  if (!pick || !frame || !portalHost) return null;
  const selected = pick.selected !== null ? geometry[pick.selected] : undefined;

  const interaction = (
    <div style={PICK_INTERACTION_STYLE} onMouseDown={down} onMouseMove={move} onMouseUp={up}>
      <div ref={draft} className="pointer-events-none absolute hidden border-2 border-[#3B82F6] bg-[#3B82F6]/10" />
    </div>
  );

  const floating = (
    <div style={PICK_FLOATING_UI_STYLE} data-recast-zone-pick>
      {geometry.map(({ number, zone, box }, i) => (
        <div
          key={zone.id}
          className={`absolute border-2 ${zone.enabled ? 'border-[#3B82F6] bg-[#3B82F6]/10' : 'border-dashed border-white/40'} ${i === pick.selected ? 'ring-2 ring-white/70' : ''}`}
          style={box}
        >
          <span className={`absolute left-1 top-1 rounded bg-[#2A2A2A] px-1 text-[10px] ${zone.enabled ? 'text-white' : 'text-white/40'}`}>{number}</span>
        </div>
      ))}
      {selected && (
        <input
          key={pick.selected}
          value={selected.zone.note}
          onChange={changeNote}
          onMouseDown={swallow}
          onPointerDown={swallow}
          placeholder={t.note(selected.number)}
          aria-label={t.note(selected.number)}
          className="pointer-events-auto absolute -translate-x-1/2 rounded-md border border-white/10 bg-[#2A2A2A] px-2 py-1 text-xs text-white outline-none placeholder:text-white/40 focus:border-white/30"
          style={selected.noteStyle}
        />
      )}
      <div className="pointer-events-auto" style={pickToolbarStyle(frame, panX, panY, zoom, portalHost)} onMouseDown={swallow} onPointerDown={swallow}>
        <div className="flex flex-col gap-1 rounded-lg bg-[#2A2A2A] p-1 text-xs text-white shadow-lg">
          <div className="flex items-center justify-between gap-2 px-1 py-1">
            {detecting
              ? <span className="flex items-center gap-1 text-white/50"><Loader2 size={12} className="animate-spin" />{t.detecting}</span>
              : <span className="text-white/50">{t.zones(enabled, pick.zones.length)}</span>}
            {!detecting && credits !== null && <CreditsBadge value={String(credits)} size="sm" />}
          </div>
          {!detecting && errors.length > 0 && <p className="px-1 text-white/50">{errors.join(' ')}</p>}
          <div className="flex items-center justify-end gap-1">
            <OverlayActionButton onClick={recast} title={detecting ? t.detecting : errors[0] ?? t.recast(enabled)} icon={Check} isActive={!detecting && !errors.length} disabled={detecting || errors.length > 0} />
            <OverlayActionButton onClick={detectAgain} title={detecting ? t.detecting : t.detectAgain} icon={RefreshCw} disabled={detecting} />
            <OverlayActionButton onClick={cancel} title={t.cancel} icon={X} />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(interaction, portalHost)}
      {createPortal(floating, portalHost)}
    </>
  );
});

RecastZonePickOverlay.displayName = 'RecastZonePickOverlay';
export default RecastZonePickOverlay;
